import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CelebrationStack from '../components/CelebrationToast';
import { storage } from '../utils/platform';

// Whitelist d'origines de confiance pour le postMessage JWT.
// En cas d'origin inattendue (DOM clobbering / attaque), fallback prod.
// ⚠️ AJOUTER UN DOMAINE ICI = revue de code obligatoire.
const TRUSTED_DASHBOARD_ORIGINS = [
  'https://www.getrevyo.com',
  'https://getrevyo.com',
  'http://localhost:3000',
  'http://127.0.0.1:3000',
];

function getTrustedTargetOrigin() {
  const current = window.location.origin;
  return TRUSTED_DASHBOARD_ORIGINS.includes(current)
    ? current
    : 'https://www.getrevyo.com';
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [authorized, setAuthorized] = useState(false);
  const [banner, setBanner] = useState(null); // { type: 'success'|'cancel', text }
  const iframeRef = useRef(null);

  // Passe le token à l'iframe via postMessage dès qu'il est chargé.
  // async pour lire storage.get (Preferences natif en iOS / localStorage en web).
  const handleIframeLoad = async () => {
    const token = await storage.get('token');
    if (token && iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'revyo-auth', token },
        getTrustedTargetOrigin()
      );
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await storage.get('token');
      if (cancelled) return;
      if (!token) {
        navigate('/login');
      } else {
        setAuthorized(true);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => {
    const paiement = searchParams.get('paiement');
    if (paiement === 'success') {
      setBanner({ type: 'success', text: '🎉 Abonnement activé ! Bienvenue dans Revyo.' });
    } else if (paiement === 'cancel') {
      setBanner({ type: 'cancel', text: 'Paiement annulé. Vous pouvez vous abonner à tout moment.' });
    }
    if (paiement) {
      // Nettoie l'URL après lecture
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('paiement');
      setSearchParams(newParams, { replace: true });

      const t = setTimeout(() => setBanner(null), 5000);
      return () => clearTimeout(t);
    }
  }, [searchParams, setSearchParams]);

  if (!authorized) return null;

  const bannerStyle = banner && {
    position: 'fixed',
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    padding: '14px 24px',
    borderRadius: 10,
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 14,
    fontWeight: 600,
    boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
    maxWidth: '90vw',
    textAlign: 'center',
    background: banner.type === 'success' ? '#DCFCE7' : '#FFEDD5',
    color: banner.type === 'success' ? '#166534' : '#9A3412',
    border: '1px solid ' + (banner.type === 'success' ? '#86EFAC' : '#FDBA74')
  };

  // feat/celebrations-v1 — propagation du ?jump=insights|graph à l'iframe :
  // Dashboard.js lit le param du parent, l'iframe le lit de son propre src
  // et déclenche allerAuxInsights() ou scrollIntoView sur la card chart.
  const jumpParam = searchParams.get('jump');
  const iframeSrc = '/dashboard-static.html?v=' + Date.now()
    + (jumpParam ? '&jump=' + encodeURIComponent(jumpParam) : '');

  return (
    <>
      {banner && <div style={bannerStyle}>{banner.text}</div>}
      <CelebrationStack />
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Tableau de bord"
        onLoad={handleIframeLoad}
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          display: 'block'
        }}
      />
    </>
  );
}
